import { useNavigate } from "react-router-dom";
import {
  Ticket,
  TrendingUp,
  Users,
  DollarSign,
  Award,
  ArrowRight,
  BadgePercent,
  Lock,
  Crown,
} from "lucide-react";

const DashboardContentWithOverlay = ({ 
  stats, 
  financialStats, 
  businessInfo, 
  showOverlay = false 
}) => {
  const navigate = useNavigate();

  return (
    <div className="relative">
      {/* Stats Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 ${showOverlay ? 'filter blur-sm' : ''}`}>
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`theme-card rounded-lg p-6 ${stat.bgColor}`}>
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.iconBg} p-3 rounded-lg`}>
                  <Icon className={`h-6 w-6 ${stat.textColor}`} />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">
                {stat.label}
              </h3>
              <p className={`text-3xl font-bold ${stat.textColor}`}>
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Two Column Layout */}
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 ${showOverlay ? 'filter blur-sm' : ''}`}>
        {/* Financial Insights */}
        <div className="theme-card rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              Financial Insights
            </h2>
            <TrendingUp className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <p className="text-sm text-green-700">Net Earnings</p>
                <p className="text-xl font-bold text-green-600">
                  {financialStats.net_earnings.formatted}
                </p>
              </div>
              <div className="bg-green-100 p-2 rounded-full">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div>
                <p className="text-sm text-red-700">Platform Commission</p>
                <p className="text-lg font-bold text-red-600">
                  {financialStats.platform_commission.formatted}
                </p>
              </div>
              <div className="bg-red-100 p-2 rounded-full">
                <TrendingUp className="h-5 w-5 text-red-600" />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
              <div>
                <p className="text-sm text-purple-700">Customer Discount</p>
                <p className="text-lg font-bold text-purple-600">
                  {financialStats.customer_savings.formatted}
                </p>
              </div>
              <div className="bg-purple-100 p-2 rounded-full">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate("/business/transactions/history")}
            className="w-full mt-4 text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center justify-center space-x-2 py-2"
          >
            <span>View Transaction History</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          <div className="theme-card rounded-lg p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Quick Actions
            </h2>
            <div className="space-y-3">
              {businessInfo.is_premium ? (
                <>
                  <button
                    onClick={() => navigate("/business/transactions")}
                    className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <Ticket className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-gray-900">
                        New Transaction
                      </span>
                    </div>
                    <ArrowRight className="h-5 w-5 text-blue-600" />
                  </button>
                  <button
                    onClick={() => navigate("/business/discount")}
                    className="w-full flex items-center justify-between p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <BadgePercent className="h-5 w-5 text-orange-600" />
                      <span className="font-medium text-gray-900">
                        Manage Discount
                      </span>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => navigate("/business/discount")}
                  className="w-full flex items-center justify-between p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors text-left"
                >
                  <div className="flex items-center space-x-3">
                    <BadgePercent className="h-5 w-5 text-orange-600" />
                    <span className="font-medium text-gray-900">
                      Manage Discount
                    </span>
                  </div>
                  <ArrowRight className="h-5 w-5 text-orange-600" />
                </button>
              )}
              <button
                onClick={() => navigate("/business/settings")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <Award className="h-5 w-5 text-gray-600" />
                  <span className="font-medium text-gray-900">
                    Business Settings
                  </span>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for Basic Users */}
      {showOverlay && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center p-8 bg-white rounded-lg shadow-xl border border-gray-200 max-w-md mx-4">
            <Lock className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Features Locked</h3>
            <p className="text-gray-600 mb-6">
              Unlock advanced analytics, transaction management, and detailed insights with Premium subscription
            </p>
            {/* <button
              onClick={() => navigate("/business/discount")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-md w-full"
            >
              <Crown className="h-5 w-5 inline mr-2" />
              Upgrade to Premium
            </button> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardContentWithOverlay;